import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2, ArrowRight, Mail, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Translations
const TRANSLATIONS = {
  en: {
    verifying: "Verifying your email...",
    success: "Your email has been verified!",
    successMessage: "You can now log in to your account.",
    failed: "Verification Failed",
    failedMessage: "The verification link may have expired or is invalid.",
    alreadyVerified: "Email Already Verified",
    alreadyVerifiedMessage: "Your email was already verified. You can log in now.",
    redirecting: "Redirecting to login in",
    goToLogin: "Go to Login",
    resendVerification: "Request New Verification Link"
  },
  ms: {
    verifying: "Mengesahkan emel anda...",
    success: "Emel anda telah disahkan!",
    successMessage: "Anda boleh log masuk ke akaun anda sekarang.",
    failed: "Pengesahan Gagal",
    failedMessage: "Pautan pengesahan mungkin telah tamat tempoh atau tidak sah.",
    alreadyVerified: "Emel Telah Disahkan",
    alreadyVerifiedMessage: "Emel anda telah disahkan sebelum ini. Anda boleh log masuk sekarang.",
    redirecting: "Mengalihkan ke log masuk dalam",
    goToLogin: "Pergi ke Log Masuk",
    resendVerification: "Minta Pautan Pengesahan Baharu"
  },
  ta: {
    verifying: "உங்கள் மின்னஞ்சலை சரிபார்க்கிறது...",
    success: "உங்கள் மின்னஞ்சல் சரிபார்க்கப்பட்டது!",
    successMessage: "இப்போது உங்கள் கணக்கில் உள்நுழையலாம்.",
    failed: "சரிபார்ப்பு தோல்வி",
    failedMessage: "சரிபார்ப்பு இணைப்பு காலாவதியாகி அல்லது செல்லாததாக இருக்கலாம்.",
    alreadyVerified: "மின்னஞ்சல் ஏற்கனவே சரிபார்க்கப்பட்டது",
    alreadyVerifiedMessage: "உங்கள் மின்னஞ்சல் ஏற்கனவே சரிபார்க்கப்பட்டது. இப்போது உள்நுழையலாம்.",
    redirecting: "உள்நுழைவுக்கு திருப்பிவிடுகிறது",
    goToLogin: "உள்நுழைவுக்கு செல்",
    resendVerification: "புதிய சரிபார்ப்பு இணைப்பைக் கோருங்கள்"
  },
  hi: {
    verifying: "आपका ईमेल सत्यापित हो रहा है...",
    success: "आपका ईमेल सत्यापित हो गया है!",
    successMessage: "अब आप अपने खाते में लॉग इन कर सकते हैं।",
    failed: "सत्यापन विफल",
    failedMessage: "सत्यापन लिंक समाप्त हो गया हो सकता है या अमान्य है।",
    alreadyVerified: "ईमेल पहले से सत्यापित है",
    alreadyVerifiedMessage: "आपका ईमेल पहले ही सत्यापित हो चुका है। अब आप लॉग इन कर सकते हैं।",
    redirecting: "लॉगिन पर रीडायरेक्ट हो रहा है",
    goToLogin: "लॉगिन पर जाएं",
    resendVerification: "नया सत्यापन लिंक अनुरोध करें"
  },
  bn: {
    verifying: "আপনার ইমেল যাচাই করা হচ্ছে...",
    success: "আপনার ইমেল যাচাই করা হয়েছে!",
    successMessage: "এখন আপনি আপনার অ্যাকাউন্টে লগ ইন করতে পারেন।",
    failed: "যাচাইকরণ ব্যর্থ",
    failedMessage: "যাচাইকরণ লিঙ্কের মেয়াদ শেষ হয়ে থাকতে পারে বা অবৈধ।",
    alreadyVerified: "ইমেল আগেই যাচাই করা হয়েছে",
    alreadyVerifiedMessage: "আপনার ইমেল আগেই যাচাই করা হয়েছিল। এখন আপনি লগ ইন করতে পারেন।",
    redirecting: "লগইনে রিডাইরেক্ট করা হচ্ছে",
    goToLogin: "লগইনে যান",
    resendVerification: "নতুন যাচাইকরণ লিঙ্ক অনুরোধ করুন"
  },
  ne: {
    verifying: "तपाईंको इमेल प्रमाणित गर्दै...",
    success: "तपाईंको इमेल प्रमाणित भयो!",
    successMessage: "अब तपाईं आफ्नो खातामा लग इन गर्न सक्नुहुन्छ।",
    failed: "प्रमाणीकरण असफल",
    failedMessage: "प्रमाणीकरण लिंक समाप्त भएको वा अमान्य हुन सक्छ।",
    alreadyVerified: "इमेल पहिले नै प्रमाणित छ",
    alreadyVerifiedMessage: "तपाईंको इमेल पहिले नै प्रमाणित थियो। अब तपाईं लग इन गर्न सक्नुहुन्छ।",
    redirecting: "लगइनमा रिडाइरेक्ट गर्दै",
    goToLogin: "लगइनमा जानुहोस्",
    resendVerification: "नयाँ प्रमाणीकरण लिंक अनुरोध गर्नुहोस्"
  },
  my: {
    verifying: "သင့်အီးမေးလ်ကို အတည်ပြုနေသည်...",
    success: "သင့်အီးမေးလ်ကို အတည်ပြုပြီးပါပြီ!",
    successMessage: "ယခု သင့်အကောင့်သို့ လော့ဂ်အင်ဝင်နိုင်ပါပြီ။",
    failed: "အတည်ပြုခြင်း မအောင်မြင်ပါ",
    failedMessage: "အတည်ပြုလင့်ခ် သက်တမ်းကုန်သွားနိုင်သည် သို့မဟုတ် မမှန်ကန်ပါ။",
    alreadyVerified: "အီးမေးလ် အတည်ပြုပြီးဖြစ်သည်",
    alreadyVerifiedMessage: "သင့်အီးမေးလ်ကို အရင်က အတည်ပြုပြီးဖြစ်သည်။ ယခု လော့ဂ်အင်ဝင်နိုင်ပါပြီ။",
    redirecting: "လော့ဂ်အင်သို့ ပြန်ညွှန်းနေသည်",
    goToLogin: "လော့ဂ်အင်သို့ သွားပါ",
    resendVerification: "အတည်ပြုလင့်ခ်အသစ် တောင်းဆိုပါ"
  }
};

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, already_verified, failed
  const [countdown, setCountdown] = useState(5);
  const [errorMessage, setErrorMessage] = useState("");
  
  const email = searchParams.get("email");
  const token = searchParams.get("token");
  
  // Get language from localStorage or default to English
  const savedLang = localStorage.getItem('myshield_lang') || 'en';
  const t = TRANSLATIONS[savedLang] || TRANSLATIONS.en;

  useEffect(() => {
    const verifyEmail = async () => {
      if (!email || !token) {
        setStatus("failed");
        setErrorMessage("Missing verification parameters");
        return;
      }

      try {
        const response = await axios.post(`${BACKEND_URL}/api/auth/verify-email`, {
          email,
          token
        });

        if (response.data.already_verified) {
          setStatus("already_verified");
        } else {
          setStatus("success");
        }
      } catch (error) {
        setStatus("failed");
        setErrorMessage(error.response?.data?.detail || "Verification failed");
      }
    };

    verifyEmail();
  }, [email, token]);

  // Countdown and redirect for success
  useEffect(() => {
    if ((status === "success" || status === "already_verified") && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if ((status === "success" || status === "already_verified") && countdown === 0) {
      navigate("/login");
    }
  }, [status, countdown, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-[#0A1128] to-black p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md glass-card p-8 relative z-10 animate-fade-in text-center" data-testid="verify-email-card">
        {/* Logo */}
        <img 
          src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
          alt="MyShield Logo" 
          className="h-14 w-auto mx-auto mb-6"
        />

        {/* Verifying State */}
        {status === "verifying" && (
          <div className="space-y-4">
            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto">
              <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-white">{t.verifying}</h1>
          </div>
        )}

        {/* Success State */}
        {status === "success" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto glow-green">
              <CheckCircle className="w-10 h-10 text-emerald-400" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">{t.success}</h1>
              <p className="text-white/60">{t.successMessage}</p>
            </div>
            <div className="text-sm text-white/40">
              {t.redirecting} {countdown}s...
            </div>
            <Link to="/login">
              <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl" data-testid="go-to-login">
                {t.goToLogin}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}

        {/* Already Verified State */}
        {status === "already_verified" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-cyan-400" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">{t.alreadyVerified}</h1>
              <p className="text-white/60">{t.alreadyVerifiedMessage}</p>
            </div>
            <div className="text-sm text-white/40">
              {t.redirecting} {countdown}s...
            </div>
            <Link to="/login">
              <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl" data-testid="go-to-login">
                {t.goToLogin}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}

        {/* Failed State */}
        {status === "failed" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-400" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">{t.failed}</h1>
              <p className="text-white/60">{t.failedMessage}</p>
              {errorMessage && (
                <p className="text-red-400 text-sm mt-2">{errorMessage}</p>
              )}
            </div>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl" data-testid="go-to-login">
                  {t.goToLogin}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
